import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { CHECKOUT_SUCCESS } from "@/constants/testIds";
import { LOGO_URL } from "@/data/brand";
import { getTier } from "@/data/pricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id");
  const [booking, setBooking] = useState(null);
  const [phase, setPhase] = useState(bookingId ? "loading" : "missing");

  useEffect(() => {
    let cancelled = false;
    if (!bookingId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bookings/${bookingId}`);
        if (!cancelled) {
          setBooking(data);
          setPhase("received");
        }
      } catch {
        if (!cancelled) setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  const tier = booking ? getTier(booking.load_size) : null;

  return (
    <div data-testid={CHECKOUT_SUCCESS.root} className="min-h-screen bg-[var(--kay-bg)] text-black">
      <header className="border-b-2 border-black bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Kay's Waste Management"
              className="h-11 w-11 rounded-full border-2 border-black bg-black object-cover"
            />
            <div className="leading-none">
              <div className="font-script text-xl text-[var(--kay-pink)]">Kay's</div>
              <div className="font-display text-[13px] uppercase tracking-widest text-[var(--kay-pink)]">
                Waste Management
              </div>
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-2 text-xs font-semibold uppercase tracking-widest hard-shadow-sm hover-lift"
          >
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>
      <div className="hi-vis-stripe h-3 w-full" />

      <main className="mx-auto max-w-3xl px-4 py-16 md:px-8 md:py-24">
        <div data-testid={CHECKOUT_SUCCESS.status} className="border-2 border-black bg-white p-8 hard-shadow md:p-12">
          {phase === "loading" && (
            <StatusBlock
              icon={<CheckCircle2 size={26} />}
              tone="pink"
              title="Loading your booking…"
              body="One moment."
            />
          )}
          {phase === "received" && booking && (
            <>
              <StatusBlock
                icon={<CheckCircle2 size={26} />}
                tone="pink"
                title="Booking received!"
                body="Thanks so much — we've got your details. Kay's team will be in touch shortly to confirm your collection. Payment is taken on collection."
              />
              <dl className="mt-8 grid grid-cols-1 gap-3 border-t-2 border-black pt-6 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs uppercase tracking-widest text-neutral-500">Reference</dt>
                  <dd className="break-all font-mono text-xs">{booking.id}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-neutral-500">Name</dt>
                  <dd className="font-semibold">{booking.name}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-neutral-500">Load</dt>
                  <dd className="font-semibold">{tier?.name || booking.load_size}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-neutral-500">Paid on collection</dt>
                  <dd className="font-semibold">£{booking.load_price}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-neutral-500">Preferred date</dt>
                  <dd className="font-semibold">{booking.preferred_date}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-neutral-500">Preferred time</dt>
                  <dd className="font-semibold">{booking.preferred_time}</dd>
                </div>
              </dl>
              <ActionRow />
            </>
          )}
          {phase === "error" && (
            <>
              <StatusBlock
                icon={<XCircle size={26} />}
                tone="black"
                title="Couldn't find that booking"
                body="We couldn't load your booking details. Please try booking again or get in touch."
              />
              <ActionRow cta />
            </>
          )}
          {phase === "missing" && (
            <>
              <StatusBlock
                icon={<XCircle size={26} />}
                tone="black"
                title="No booking reference"
                body="This page is only shown after submitting a booking. Please start a booking from our pricing page."
              />
              <ActionRow cta />
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function StatusBlock({ icon, title, body, tone }) {
  const toneCls =
    tone === "pink"
      ? "bg-[var(--kay-pink)] text-black"
      : tone === "yellow"
      ? "bg-yellow-300 text-black"
      : "bg-black text-white";
  return (
    <>
      <div className={`inline-flex h-14 w-14 items-center justify-center border-2 border-black ${toneCls}`}>
        {icon}
      </div>
      <h1 className="mt-6 font-display text-3xl uppercase leading-tight sm:text-4xl md:text-5xl">
        {title}
      </h1>
      <p className="mt-4 max-w-xl text-neutral-700 sm:text-lg">{body}</p>
    </>
  );
}

function ActionRow({ cta }) {
  return (
    <div className="mt-10 flex flex-wrap items-center gap-3">
      <Link
        to="/"
        className="border-2 border-black bg-white px-5 py-3 font-display text-xs uppercase tracking-widest hard-shadow-sm hover-lift"
      >
        Back to home
      </Link>
      {cta && (
        <Link
          to="/checkout"
          className="border-2 border-black bg-[var(--kay-pink)] px-5 py-3 font-display text-xs uppercase tracking-widest text-black hard-shadow-sm hover-lift"
        >
          Start a booking
        </Link>
      )}
    </div>
  );
}
